class ImageWithFallback extends HTMLElement {
  /**
   * @type {keyof typeof Theme.fallbacks}
   */
  #type;
  /**
   * @type {string}
   */
  #fallbackClass = 'image__fallback';
  /**
   * @type {HTMLImageElement | null}
   */
  _img;

  static get observedAttributes() { return ['type']; }

  constructor() {
    super();

    this.onError = this.onError.bind(this);

    this.attachShadow({ mode: 'open' }).innerHTML = `<slot></slot>`;
    this._slot = this.shadowRoot?.querySelector('slot');
    this._onSlotChange = () => this._syncImage();
    this._slot?.addEventListener('slotchange', this._onSlotChange);
    this._mutationObserver = new MutationObserver((muts) => this._handleMutations(muts));
  }

  connectedCallback() {
    const type = this.getAttribute('type');
    if (type) {
      if (type in Theme.fallbacks) {
        // @ts-ignore
        this.#type = type;
      } else {
        console.warn(`Unsupported fallback type: ${type}`);
      }
    } else {
      console.warn('No fallback type specified');
    }

    this._mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src'],
    });
    this._syncImage();
  }

  disconnectedCallback() {
    this._mutationObserver.disconnect();
    this._slot?.removeEventListener('slotchange', this._onSlotChange);
    if (this._img) this._detachFrom(this._img);
    this._img = null;
  }

  /**
   * @param {string} name
   * @param {string | null} oldVal
   * @param {string | null} newVal
   */
  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'type' && oldVal !== newVal) {
      const type = this.getAttribute('type');
      if (type && type in Theme.fallbacks) {
        // @ts-ignore
        this.#type = type;
      } else {
        console.warn(`Unsupported fallback type: ${type}`);
      }
      // If we already applied a fallback, switch it to the new type's fallback.
      if (this._img && this._img.dataset.fallbackApplied === '1') {
        const fb = this._resolveUrl(this._getFallback());
        if (fb && this._img.src !== fb) this._img.src = fb;
      }
    }
  }

  _getFallback() {
    return Theme.fallbacks[this.#type];
  }

  /**
   * @param {string} u
   */
  _resolveUrl(u) {
    try { return new URL(u, document.baseURI).href; } catch { return u; }
  }

  /**
   * @param {MutationRecord[]} muts
   */
  _handleMutations(muts) {
    let resync = false;
    for (const m of muts) {
      if (m.type === 'childList') {
        resync = true;
      } else if (m.type === 'attributes' && m.attributeName === 'src') {
        // If our tracked <img> changed src, allow fallback again for the new URL
        if (m.target === this._img) {
          const fallback = this._getFallback();
          const resolved = fallback ? this._resolveUrl(fallback) : '';
          const isFallbackSrc = resolved && this._img.src === resolved;
          const hasAppliedFallback = this._img.dataset.fallbackApplied === '1';

          if (hasAppliedFallback) {
            if (isFallbackSrc) {
              this._img.classList.add(this.#fallbackClass);
            } else {
              delete this._img.dataset.fallbackApplied;
              this._img.classList.remove(this.#fallbackClass);
            }
          } else if (isFallbackSrc) {
            this._img.dataset.fallbackApplied = '1';
            this._img.classList.add(this.#fallbackClass);
          }
        }
        // If some other <img> appeared/changed, rescan
        if (!this._img) resync = true;
      }
    }
    if (resync) this._syncImage();
  }

  _syncImage() {
    // Use first <img> inside the element (light DOM)
    const img = this.querySelector('img');
    if (img === this._img) return;

    if (this._img) this._detachFrom(this._img);
    this._img = img || null;
    if (this._img) this._attachTo(this._img);
  }

  /**
   * @param {HTMLImageElement} img
   */
  _attachTo(img) {
    img.addEventListener('error', this.onError);
    // If it already failed before we subscribed, detect and apply fallback.
    if (img.complete && img.naturalWidth === 0) {
      this._applyFallback(img);
    }
  }

  /**
   * @param {HTMLImageElement} img
   */
  _detachFrom(img) {
    img.removeEventListener('error', this.onError);
  }

  /**
   * @param {Event} e
   */
  onError(e) {
    if (!(e.currentTarget instanceof HTMLImageElement)) return;

    this._applyFallback(e.currentTarget);
  }

  /**
   * @param {HTMLImageElement} img
   */
  _applyFallback(img) {
    const fallback = this._getFallback();
    if (!fallback) return;
    if (img.dataset.fallbackApplied === '1') return; // prevent loop
    const resolved = this._resolveUrl(fallback);
    if (img.src === resolved) return;
    img.dataset.fallbackApplied = '1';
    img.classList.add(this.#fallbackClass);
    img.src = resolved;
  }
}

if (!customElements.get('image-with-fallback')) {
  customElements.define('image-with-fallback', ImageWithFallback);
}
